import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Package, AlertTriangle } from 'lucide-react';
import { useCreateGRN, type GRNLineItemInput } from '../../../api/mutations/purchasing.mutations';
import { useBranches } from '../../../api/queries/settings.queries';
import type { PurchaseOrder } from '../../../api/queries/purchasing.queries';

interface CreateGRNModalProps {
  sourcePO: PurchaseOrder | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateGRNModal({ sourcePO, onClose, onSuccess }: CreateGRNModalProps) {
  const { t } = useTranslation();

  const [warehouseId, setWarehouseId] = useState('');
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<GRNLineItemInput[]>([]);

  const { data: branches } = useBranches();
  const createGRN = useCreateGRN();

  // Initialize items from source PO
  useEffect(() => {
    if (sourcePO) {
      setWarehouseId(sourcePO.deliveryBranchId?._id || '');
      setItems(
        sourcePO.items.map((item) => ({
          poLineItemId: item._id,
          ingredientId: item.ingredientId?._id,
          customItemName: item.customItemName,
          isFreeFormItem: item.isFreeFormItem,
          orderedQuantity: item.quantity,
          receivedQuantity: item.quantity - item.receivedQuantity, // Remaining to receive
          unit: item.unit,
          condition: 'good' as const,
        }))
      );
    }
  }, [sourcePO]);

  const updateItem = (index: number, updates: Partial<GRNLineItemInput>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const getDiscrepancies = () => {
    return items.filter((item) => {
      const expected = sourcePO?.items.find((i) => i._id === item.poLineItemId);
      if (!expected) return false;
      const remaining = expected.quantity - expected.receivedQuantity;
      return item.receivedQuantity !== remaining || item.condition !== 'good';
    });
  };

  const hasDiscrepancies = getDiscrepancies().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourcePO) {
      alert(t('purchasing.noPOSelected'));
      return;
    }

    if (!warehouseId) {
      alert(t('purchasing.selectWarehouse'));
      return;
    }

    try {
      await createGRN.mutateAsync({
        poId: sourcePO._id,
        warehouseId,
        items,
        deliveryNoteNumber: deliveryNoteNumber || undefined,
        invoiceNumber: invoiceNumber || undefined,
        invoiceAmount: invoiceAmount || undefined,
        notes: notes || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create GRN:', error);
    }
  };

  if (!sourcePO) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-card rounded-lg p-6 text-center">
          <p className="text-muted-foreground">{t('purchasing.selectPOFirst')}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 rounded-lg border hover:bg-secondary">
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('purchasing.recordGRN')}</h2>
            <span className="text-sm text-muted-foreground">
              ({t('purchasing.forPO')}: {sourcePO.poNumber})
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Discrepancy Warning */}
            {hasDiscrepancies && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="text-sm">{t('purchasing.discrepancyWarning')}</p>
              </div>
            )}

            {/* Warehouse & Document Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.warehouse')} *</label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                >
                  <option value="">{t('common.select')}</option>
                  {branches?.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.deliveryNoteNumber')}</label>
                <input
                  type="text"
                  value={deliveryNoteNumber}
                  onChange={(e) => setDeliveryNoteNumber(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.invoiceNumber')}</label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.invoiceAmount')}</label>
                <input
                  type="number"
                  value={invoiceAmount || ''}
                  onChange={(e) => setInvoiceAmount(parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  min={0}
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('purchasing.receivedItems')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('purchasing.item')}</th>
                      <th className="text-right py-2 px-3 w-24">{t('purchasing.ordered')}</th>
                      <th className="text-right py-2 px-3 w-24">{t('purchasing.received')}</th>
                      <th className="text-left py-2 px-3 w-32">{t('purchasing.condition')}</th>
                      <th className="text-left py-2 px-3 w-32">{t('purchasing.batchNo')}</th>
                      <th className="text-left py-2 px-3 w-32">{t('purchasing.expiryDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const poItem = sourcePO.items.find((i) => i._id === item.poLineItemId);
                      const remaining = poItem ? poItem.quantity - poItem.receivedQuantity : 0;
                      const hasQtyDiscrepancy = item.receivedQuantity !== remaining;
                      const hasBadCondition = item.condition !== 'good';

                      return (
                        <tr key={index} className={`border-t ${hasQtyDiscrepancy || hasBadCondition ? 'bg-yellow-50' : ''}`}>
                          <td className="py-2 px-3">
                            {item.isFreeFormItem ? item.customItemName : poItem?.ingredientId?.name}
                          </td>
                          <td className="py-2 px-3 text-right">{remaining} {item.unit}</td>
                          <td className="py-2 px-3">
                            <input
                              type="number"
                              value={item.receivedQuantity}
                              onChange={(e) => updateItem(index, { receivedQuantity: parseFloat(e.target.value) || 0 })}
                              className={`w-full rounded border bg-background px-2 py-1 text-right ${hasQtyDiscrepancy ? 'border-yellow-400' : ''}`}
                              min={0}
                              step={0.1}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <select
                              value={item.condition}
                              onChange={(e) => updateItem(index, { condition: e.target.value as GRNLineItemInput['condition'] })}
                              className={`w-full rounded border bg-background px-2 py-1 ${hasBadCondition ? 'border-yellow-400' : ''}`}
                            >
                              <option value="good">{t('purchasing.good')}</option>
                              <option value="damaged">{t('purchasing.damaged')}</option>
                              <option value="expired">{t('purchasing.expired')}</option>
                              <option value="wrong_item">{t('purchasing.wrongItem')}</option>
                              <option value="quality_issue">{t('purchasing.qualityIssue')}</option>
                            </select>
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="text"
                              value={item.supplierBatchNumber || ''}
                              onChange={(e) => updateItem(index, { supplierBatchNumber: e.target.value })}
                              className="w-full rounded border bg-background px-2 py-1"
                              placeholder={t('purchasing.batchNoPlaceholder')}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="date"
                              value={item.expiryDate?.split('T')[0] || ''}
                              onChange={(e) => updateItem(index, { expiryDate: e.target.value })}
                              className="w-full rounded border bg-background px-2 py-1"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
                placeholder={t('purchasing.grnNotesPlaceholder')}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createGRN.isPending || !warehouseId}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {createGRN.isPending ? t('common.saving') : t('purchasing.saveGRN')}
          </button>
        </div>
      </div>
    </div>
  );
}
